import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { WebLogger } from "@weps/shared-http";

@Injectable({
  providedIn: 'root'
})
export class CookieManagementService {

  // Essential cookies for External (Siteminder) authentication
  private readonly externalEssentialCookies = [
    'cl_spoid',
    'cccSharedData',
    'XSRF-TOKEN'
  ];

  // Essential cookies for Internal (Okta) authentication
  private readonly internalEssentialCookies = [
    'POID',
    'XSRF-TOKEN'
  ];

  // Common essential cookies for both modes
  private readonly commonEssentialCookies = [
    'VGSESSION',
    'SOURCE',
    'OEDPRD',
    '_vgi_logon',
    '_vgi_logon2',
    '_vgi_rep',
    '_vgi_psw_prd',
    '_vgi_psw_sat',
    '_vgi_int',
    'acCookie',
    'mrcClientId',
    'cccSpoidData'
  ];

  /**
   * Gets the list of essential cookies based on the current environment
   */
  getEssentialCookieNames(): string[] {
    const authSpecificCookies = environment.internal
      ? this.internalEssentialCookies
      : this.externalEssentialCookies;

    return [...authSpecificCookies, ...this.commonEssentialCookies];
  }

  /**
   * Determines if a request should have cookie filtering applied
   */
  shouldFilterCookies(url: string, method: string): boolean {
    const graphqlEndpoints = [
      '/api/weps-profile/get-profile/graphql',
      '/api/weps-profile/subgraph/aan/graphql',
      '/api/weps-profile/subgraph/ago/graphql',
      '/api/vex/status',
      '/api/vex/estate-profile',
      '/api/estate-plan/tracker'
    ];

    return graphqlEndpoints.some(endpoint => url.includes(endpoint)) &&
           (method === 'POST' || method === 'GET');
  }

  /**
   * Removes non-essential cookies from the document
   * WARNING: This modifies the browser's cookie store
   */
  clearNonEssentialCookies(): void {
    const essentialNames = this.getEssentialCookieNames();
    const allCookies = document.cookie.split(';');

    WebLogger.log({
      message: 'Starting cookie cleanup',
      level: 'debug',
      payload: {
        event_type: 'cookie_cleanup_start',
        service: 'pwi',
        total_cookies: allCookies.length,
        essential_cookie_count: essentialNames.length
      }
    });

    let removedCount = 0;

    allCookies.forEach(cookie => {
      const cookieName = cookie.trim().split('=')[0];
      const isEssential = essentialNames.some(essential =>
        cookieName === essential || cookieName.startsWith(essential)
      );

      if (!isEssential && cookieName) {
        // Remove the cookie by setting it to expire in the past
        const expireDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
        document.cookie = `${cookieName}=; expires=${expireDate}; path=/; domain=.vanguard.com;`;
        document.cookie = `${cookieName}=; expires=${expireDate}; path=/;`;
        removedCount++;
      }
    });

    WebLogger.log({
      message: 'Cookie cleanup complete',
      level: 'info',
      payload: {
        event_type: 'cookie_cleanup_complete',
        service: 'pwi',
        removed_count: removedCount
      }
    });
  }

  /**
   * Gets the current cookie header size in bytes
   */
  getCurrentCookieSize(): number {
    return document.cookie.length;
  }

  /**
   * Logs current cookie statistics
   */
  logCookieStats(): void {
    const currentSize = this.getCurrentCookieSize();
    const essentialCount = this.getEssentialCookieNames().length;
    const totalCookies = document.cookie.split(';').filter(c => c.trim()).length;

    WebLogger.log({
      message: 'Cookie statistics',
      level: currentSize > 3000 ? 'warn' : 'debug',
      payload: {
        event_type: 'cookie_stats',
        service: 'pwi',
        total_cookie_size: currentSize,
        total_cookies: totalCookies,
        essential_count: essentialCount,
        exceeds_threshold: currentSize > 3000
      }
    });
  }

  /**
   * Manual testing method - call this from browser console to test cookie filtering
   */
  testCookieFiltering(): void {
    const essentialCookies = this.getEssentialCookieNames();
    const currentSize = this.getCurrentCookieSize();
    const shouldFilter = this.shouldFilterCookies('/api/weps-profile/get-profile/graphql', 'POST');

    WebLogger.log({
      message: 'Testing Cookie Management Service',
      level: 'info',
      payload: {
        event_type: 'cookie_filtering_test',
        service: 'pwi',
        essential_cookies: essentialCookies,
        current_size: currentSize,
        should_filter: shouldFilter,
        would_trigger_filtering: currentSize > 6144
      }
    });

    this.logCookieStats();
  }
}
